import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../../environments/environment';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CarrierService } from '../carrier/carrier.service';
import { Globals } from '../globals';
import { SelectableLab } from '../carrier/selectable-lab';
import { LabEmitterService } from '../common-services/lab-emitter.service';
import { Router } from '@angular/router';
import { AuthUtil } from '../common-services/auth-util';
import * as jwt_decode from 'jwt-decode'
import { MasterDataService } from '../masterdata/masterdata.service';
import { WorkFlow } from '../masterdata/models/workFlow';
import { ProcessFlow } from '../masterdata/models/processFlow';
import { MessagesService } from '../messages/messages.service';
import { Plate } from '../models/plate';
import { MaPlatePreparationService } from '../ma-plate-preparation/ma-plate-preparation.service';
import { BeadChipStatus, PlateStatus } from '../models/process-step';

// Header menu to handle navigation. This is dynamic menu and it has to be handled based on lab selection
// Author: Naveen
@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit {

    @ViewChild('overlayTargetMA')
    overlayTargetMA: ElementRef;

    @ViewChild('overlayTargetNGS')
    overlayTargetNGS: ElementRef;

    @ViewChild('menuItemsPanelMA')
    menuItemsPanelMA : OverlayPanel;

    @ViewChild('menuItemsPanelNGS')
    menuItemsPanelNGS : OverlayPanel;

    overrideOverlayStyle = {'background-color':'#006b96', 'border-width':'0px', 'border-color':'#006b96', 'margin-top':'30px'}

    buildNo: string;
    sampleProcessingServiceBuildNo : string;
    storageServiceBuildNo : string;
    mdmServiceBuildNo : string;
    deliveryServiceBuildNo : string;
    ngsSamplesProcessingServiceBuildNo : string;
    runPlanningServiceBuildNo : string;
    resultEvaluationServiceBuildNo

    production: boolean;
    errorHandler: ErrorHandler = new ErrorHandler;

    selectedLab: SelectableLab;
    selectedProcess: string;
    loggedUserName: string;

    isNGS: boolean = false;
    isMA: boolean = false;

    readonly COLOGNE = "COLOGNE"
    readonly AARHUS = "AARHUS"
    readonly EBERSBERG = "EBERSBERG"
    readonly MPC_EBE = "MPC_EBE"
    readonly NGS_CONSTANCE = "NGS_CONSTANCE"
    readonly APG = "APG"

    readonly BU_SANGER = "Sanger Sequencing"
    readonly BU_GENOTYPING = "Genotyping"
    readonly BU_NGS = "Next Generation Sequencing"
    readonly BU_APG = "Applied Genomics"

    validRole: boolean;
    readonly blueberryAPGCologeneReadOnlyUsers = "BlueberryAPGCologeneReadOnlyUsers";

    _opened: boolean = true

    workFlowList = [];
    workFlowData: WorkFlow[] = [];

    sideNavCollapsed: boolean = true;

    selectedComponentIndex: number = 0;
    processFlowData: ProcessFlow[] = [];
    selectedProcessFlows: ProcessFlow[] = [];

    plateToSearch: string;
    selectedPlate: Plate;
    selectedProcessFlow: ProcessFlow = null;

    mapOfStatusAndRoute: Map<string, string> = new Map<string, string>();

    constructor(private _globals: Globals, private router: Router, private _carrierService: CarrierService, private _labEmitterService: LabEmitterService,
        private masterDataService: MasterDataService, private _messagesService: MessagesService, private platePreparationService: MaPlatePreparationService) { }

    // initilizes the build info
    ngOnInit() {
        this.initializeRouteMap();
        this.selectedComponentIndex = 0;
        this.sideNavCollapsed = true;
        this.validRole = this.disableHeaderforUser();

        this._labEmitterService.change.subscribe(lab => {
            this.selectedLab = lab;
            console.log("Seleted lab in header(e): " + this.selectedLab.label)
            this.checkSelectedLab()
        });

        this.selectedLab = JSON.parse(localStorage.getItem('lab'));
        if (this.selectedLab) {
            console.log("Seleted lab in header: " + this.selectedLab.label)
            this.checkSelectedLab()
        }
        
        this.production = environment.production;
        
        setTimeout(() => {
            this.getBuildInfo();
            this.getServiceBuildInfo();            
            document.getElementById("plateID").focus()
        }, 5000);
    }

    initializeRouteMap() {
        this.mapOfStatusAndRoute.set(PlateStatus.PLATE_CREATED, 'decideOnReception');
        this.mapOfStatusAndRoute.set(PlateStatus.ANNOUNCED, 'decideOnReception');
        this.mapOfStatusAndRoute.set(PlateStatus.ALIQUOTATION_STARTED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.ALIQUOTATION_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.RECEPTION_COMPLETED, 'deciderForReceived');
        this.mapOfStatusAndRoute.set(PlateStatus.RECEIVED, 'deciderForReceived');
        this.mapOfStatusAndRoute.set(PlateStatus.QCSCAN_STARTED, 'goToPreparationQCTab');
        this.mapOfStatusAndRoute.set(PlateStatus.QCSCAN_COMPLETED, 'goToPreparationExtractionTab');
        this.mapOfStatusAndRoute.set(PlateStatus.LYSIS_STARTED, 'goToPreparationExtractionTab');
        this.mapOfStatusAndRoute.set(PlateStatus.LYSIS_COMPLETED, 'goToPreparationExtractionTab');
        this.mapOfStatusAndRoute.set(PlateStatus.EXTRACTION_STARTED, 'goToPreparationExtractionTab');
        this.mapOfStatusAndRoute.set(PlateStatus.EXTRACTION_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.MERGED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.MERGE_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.START_PREPCR, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.STORED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.WGA_STARTED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.WGA_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.PGX_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.PREPCR_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.RECOMBINING_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.RECOMBINING_STARTED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.FRAGMENTATION_STARTED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.FRAGMENTATION_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.PRECIPITATION_STARTED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.PRECIPITATION_COMPLETED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.RESUSPENDING_STARTED, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(PlateStatus.RESUSPENDING_COMPLETED, 'deciderForResuspendingCompleted');
        this.mapOfStatusAndRoute.set(PlateStatus.DENATURATION_STARTED, 'deciderForDenaturationStarted');
        this.mapOfStatusAndRoute.set(PlateStatus.DENATURATION_COMPLETED, 'goToArrayPreparationTabAsNormalPlate');
        this.mapOfStatusAndRoute.set(PlateStatus.COMPLETED, 'deciderForComplete');
        this.mapOfStatusAndRoute.set(PlateStatus.COMPLETLY_TRANSFERED, 'goToArrayPreparationTabAsNormalPlate');
        this.mapOfStatusAndRoute.set(PlateStatus.PLATE_REDO, 'goToSamplesPlatePreparationTab');
        this.mapOfStatusAndRoute.set(BeadChipStatus.LOADED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.HYBRIDIZATION_STARTED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.HYBRIDIZATION_COMPLETED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.GENE_TITAN_PREP_STARTED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.GENE_TITAN_PREP_COMPLETED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.GENE_TITAN_LOADED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.INCUBATION_STARTED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.INCUBATION_COMPLETED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.STAINING_STARTED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.STAINING_COMPLETED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.DRYING_STARTED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.DRYING_COMPLETED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.RESULT_FILE_SENT, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.BEADCHIP_IMAGED, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.WASH_AND_STAIN_PREP, 'goToArrayPreparationTabAsBeadchip');
        this.mapOfStatusAndRoute.set(BeadChipStatus.WAITING_FOR_ANALYSIS, 'deciderForWaitingForAnalysis');
        this.mapOfStatusAndRoute.set(BeadChipStatus.ANALYSIS_STARTED, 'goToResultEvaluationTab');
        this.mapOfStatusAndRoute.set(BeadChipStatus.DELIVERED, 'deciderForComplete');
    }

    openComponent(index: number){
        if(index<0){
            return;
        }
        this.selectedComponentIndex = index;
    }

    changeWorkflow(index: number){
        if(index>=0&&index<this.workFlowList.length){
            this.workFlowList.forEach(wf=>{
                wf.selected=false;
            })
            this.workFlowList[index].selected=true;
            localStorage.setItem("selectedWorkFlow", JSON.stringify(this.workFlowData[index]));
            this.selectedProcessFlows = [];
            this.processFlowData.forEach(
                processFlow => {
                  if(this.workFlowData[index].processFlow.includes(processFlow.processFlowName))
                    this.selectedProcessFlows.push(processFlow);
                }
            );
            localStorage.setItem("selectedProcessFlows", JSON.stringify(this.selectedProcessFlows));
        }
    }

    showComponentNotch(routeLink: string){
        if(this.router.url.includes(routeLink)){
            return true;
        }
        return false;
    }

    getSideNavStyle(){
        if(this.sideNavCollapsed){
            return 'page-side-bar-collapsed';
        }
        return 'page-side-bar';
    }

    getPageContentStyle(){
        if(this.sideNavCollapsed){
            return 'page-content-collapsed';
        }
        return 'page-content';
    }

    extendSideNav(event, app: string){
        if(app==='MA'){
            this.menuItemsPanelMA.show(event, this.overlayTargetMA.nativeElement)
        }else{
            this.menuItemsPanelNGS.show(event, this.overlayTargetNGS.nativeElement)
        }
    }

    collapseSideNav(){
        if(this.menuItemsPanelMA){
            this.menuItemsPanelMA.hide()
        }
        if(this.menuItemsPanelNGS){
            this.menuItemsPanelNGS.hide()
        }
    }

    getProcessFlowData(labId: string, workFlow: WorkFlow){
        this.processFlowData = [];
        this.selectedProcessFlows = [];
        this.masterDataService.getConfigList(labId, "processFlow", 0, 100, true).subscribe({
            next : result => {
                this.processFlowData = result.data;
                if(this.processFlowData.length > 0) {
                    this.processFlowData.forEach(
                        processFlow => {
                          if(workFlow.processFlow?.includes(processFlow.processFlowName))
                            this.selectedProcessFlows.push(processFlow);
                        }
                    );
                    localStorage.setItem("processFlowData", JSON.stringify(this.processFlowData));
                    localStorage.setItem("selectedProcessFlows", JSON.stringify(this.selectedProcessFlows));
                    this.decideOnRouteAndTab();
                } else {
                    this._messagesService.addErrorMessage({
                        severity:"error", summary:"Error while fetching processflows!",
                        detail: "Could not fetch process flows from MDM. " +
                         "Please retry scanning plate. Or check if MDM is configured properly"
                    });
                }
            },
            error: error => {
                this._messagesService.addErrorMessage({
                    severity:"error", summary:"Error while fetching processflows!",
                    detail: error
                })
            }
        })
    }

    getWorkFlowList(labId: string) {
        this.workFlowData = []
        this.workFlowList = []
        let configName = "";
        if (this.isMA)
            configName = "workflow"
        else
            configName = "workflowngs"
        this.masterDataService.getConfigList(labId, configName, 0, 100, true).subscribe({
            next: results => {
                this.workFlowData = results.data;
                if (this.workFlowData && this.workFlowData.length > 0) {
                    this.workFlowData.forEach(e => {
                        this.workFlowList.push({
                            label: e.workFlowName,
                            value: e.workFlowName,
                            selected: false
                        });
                    });

                    // allow setting of worklist and processflows into memory -> if user goes to sidebar there would still be something to see.
                    // But absolutely do it better it's done now.

                    if (this.workFlowList.length > 0) {
                        localStorage.setItem("workFlowData", JSON.stringify(this.workFlowData));
                        this.workFlowList[0].selected = true;
                        localStorage.setItem("selectedWorkFlow", JSON.stringify(this.workFlowData[0]));
                        this.getProcessFlowData(labId, this.workFlowData[0]);
                    }
                }
            }, error: (error: any) => {
                this.workFlowData = []
                this.workFlowList = []
                console.log("Failed to load workflows", error)
            }
        });
    }

    get loginStatus() {
        return localStorage.getItem('currentUser') ? true : false;
    }

    get azureLoginStatus() {
        let authUtil = new AuthUtil()
        let accessToken = authUtil.getAccessToken()
        if(accessToken)
        {
            let access_token = jwt_decode(accessToken);
            this.loggedUserName= access_token.name;
            let loggedUserPUID = this.getPUIDFromUPN(access_token.unique_name);
            localStorage.setItem('PUID', loggedUserPUID);
        }
        return accessToken ? true : false
    }

    logOut() {
        localStorage.removeItem('currentUser');
    }

    azureLogOut() {
        const util = new AuthUtil()
        util.logout()

    }

    // checks the selected lab
    checkSelectedLab() {
        this.selectedProcess = localStorage.getItem('process');
        this.selectedComponentIndex = 0;
        if(this.selectedProcess==='NGS'){
            this.isNGS = true;
            this.isMA = false;
        }else{
            this.isMA = true;
            this.isNGS = false;
        }
        this.getWorkFlowList(this.selectedLab.lab._id)
    }

    // chnages the lab and loads lab page
    changeLab() {
        this.isMA = false;
        this.isNGS = false;

        localStorage.removeItem('lab');
        localStorage.removeItem('selectedWorkFlow');
        this.router.navigate(['/']).then(nav => {
            console.log(nav); // true if navigation is successful
            if (nav === null) {
                console.log("Reloading...");
                window.location.reload();
            }
        }, err => {
            window.location.reload();
            console.log(err) // when there's an error
        });
    }

    getBuildInfo() {
        console.log("Pulling build info");
        this._carrierService.getBuildInfo().subscribe({
            next: results => {
                console.log('BUILD: ' + results.Build.Number);
                if (results.Build.Number.length > 0) {
                    this.buildNo = results.Build.Number;
                }
            },
            error: _error => {
                this.buildNo = '== DEV =='
            }
        });
    }

    // service call for the build info
    getServiceBuildInfo() {

        this._carrierService.getServiceBuildInfo(environment.BB_SAMPLE_PROCESSING_API).subscribe({
            next: results => {
                console.log("Samples processing service : " + results.Build?.Number);
                this.sampleProcessingServiceBuildNo = results.data.Build?.Number;
            },
            error: _error => {
                this.sampleProcessingServiceBuildNo = 'N/A'
            }}
        );

        this._carrierService.getServiceBuildInfo(environment.BB_STORAGE_URL).subscribe({
            next: results => {
                console.log("Storage service:" + results.Build?.Number);
                this.storageServiceBuildNo = results.Build?.Number;
            },
            error: _error => {
                this.storageServiceBuildNo = 'N/A'
            }}
        );

        this._carrierService.getServiceBuildInfo(environment.BB_MDMSERVICE_URL).subscribe({
            next: results => {
                console.log("MDM service: " + results?.data.Build.Number);
                this.mdmServiceBuildNo = results?.data.Build.Number;
            },
            error: _error => {
                this.mdmServiceBuildNo = 'N/A'
            }}
        );

        this._carrierService.getServiceBuildInfo(environment.BB_MA_DELIVERY_API).subscribe({
            next: results => {
                console.log("MA DELIVERY SERVICE: "+ results?.data.Build.Number);
                this.deliveryServiceBuildNo = results?.data.Build.Number;
            },
            error: _error => {
                this.deliveryServiceBuildNo = 'N/A'
            }}
        )

        this._carrierService.getServiceBuildInfo(environment.BB_APG_SAMPLE_PROCESSING_URL).subscribe({
            next: results => {
                console.log("MA NGS SAMPLES SERVICE: "+ results?.data.Build.Number);
                this.ngsSamplesProcessingServiceBuildNo = results?.data.Build.Number;
            },
            error: _error => {
                this.ngsSamplesProcessingServiceBuildNo = 'N/A'
            }}
        )

        this._carrierService.getServiceBuildInfo(environment.BB_NGS_RUN_PLANNING_URL).subscribe({
            next: results => {
                console.log("MA RUN PLANNING SERVICE: "+ results?.data.Build.Number);
                this.runPlanningServiceBuildNo = results?.data.Build.Number;
            },
            error: _error => {
                this.runPlanningServiceBuildNo = 'N/A'
            }}
        )

        this._carrierService.getServiceBuildInfo(environment.BB_RESULT_EVALUATION_URL).subscribe({
            next: results => {
                console.log("MA RUN PLANNING SERVICE: "+ results?.data.Build.Number);
                this.resultEvaluationServiceBuildNo = results?.data.Build.Number;
            },
            error: _error => {
                this.resultEvaluationServiceBuildNo = 'N/A'
            }}
        )
    }

    // binding build info
    showBuildInfo(event, overlaypanel: OverlayPanel) {
        overlaypanel.toggle(event);
    }

    getPUIDFromUPN(upn: string) {
        return upn.includes('@') ? upn.substring(0, upn.indexOf('@')) : '';
    }
    disableHeaderforUser(){
        let roles = this.getRolesFromAccessToken();
        return this.checkIfReadonlyUser(roles);
    }
    checkIfReadonlyUser(roles: any) {
        return (roles.includes(this.blueberryAPGCologeneReadOnlyUsers));
    }
    getRolesFromAccessToken() {
        let authUtil = new AuthUtil();
        let accessToken = authUtil.getAccessToken();
        if (accessToken) {
          let access_token = jwt_decode(accessToken);
          return access_token.roles;
        }
        return [];
    }

    searchForPlate() {
        // determine the view -> from the plate that is fetched -> already have the worklist and processflow data
        this.platePreparationService.fetchPlate(this.plateToSearch, this.selectedLab.lab._id).subscribe({
            next: (result) => {
                this.selectedPlate = result;
                this.plateToSearch = null;
                // evaluate workflow
                // evaluate processflow from sampletype
                // set workflow and processflow as they are being set
                // figure out the manual click situation
                
                if(this.selectedPlate.workFlow) {
                    this.triggerProcesflowFetchingForPlateWorkflow();
                } else {
                    this._messagesService.addErrorMessage({severity:'error', summary: 'Plate Does not have workflow',
                    detail:''})
                }
            }, error: (error) => {
              this._messagesService.addErrorMessage({
                severity: 'error', summary: 'Failed to fetch plate info',
                detail: error.error
              });
            }
        });
    }

    triggerProcesflowFetchingForPlateWorkflow() {
        let selectedWorkFlow: WorkFlow = null;
        for(let workFlow of this.workFlowData) {
            if(this.selectedPlate && (workFlow.workFlowName == this.selectedPlate.workFlow)) {
                selectedWorkFlow = workFlow;
                localStorage.setItem("selectedWorkFlow", JSON.stringify(selectedWorkFlow));
                this.getProcessFlowData(this.selectedLab.lab._id, workFlow);
                break;
            }
        }
    }

    decideOnRouteAndTab() {
        if(!this.selectedPlate) {
            return;
        }

        if(!this.findProcessFlowForPlate()) {
            this._messagesService.addErrorMessage({
                severity: 'error', summary: 'Failed to find process flow',
                detail: 'Please verify plate has valid sample type and procesflow has been configured in MDM'
            });
            return;
        }
        let functionName = this.mapOfStatusAndRoute.get(this.selectedPlate.status);
        if(functionName) {
            this[functionName]();
        } else {
            this._messagesService.addErrorMessage({
                severity: 'error', summary: 'Failed to find appropriate route',
                detail: 'Please verify plate has valid status'
            });
        }
    }

    findProcessFlowForPlate() {
        this.selectedProcessFlow = null;
        
        let processFlowName = this.selectedPlate.processFlowName;
        this.selectedProcessFlows.forEach(processFlow => {
            if(processFlow.processFlowName == processFlowName) {
                this.selectedProcessFlow = processFlow;
            }
        });
        return this.selectedProcessFlow;
    }

    // DECIDERS
    // Functions that can need to figure out tabs

    decideOnReception() {
        if(this.selectedProcessFlow.singleReception && this.selectedProcessFlow.manualReception) {
            this.router.navigate(['/reception', this.selectedPlate.plateID, '1']);
        } else {
            this.router.navigate(['/reception', this.selectedPlate.plateID, '0']);
        }
    }

    deciderForReceived() {
        if(this.selectedProcessFlow.qcScan) {
            this.router.navigate(['/preparation', this.selectedPlate.plateID, '0']);
        } else if(!this.selectedProcessFlow.qcScan && (this.selectedProcessFlow.ExEartagTLR || this.selectedProcessFlow.okTOpureHair ||
                this.selectedProcessFlow.okTOpureEartag)){
            this.router.navigate(['/preparation', this.selectedPlate.plateID, '1']);
        } else {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '0', 'PLATE']);
        }
    }

    deciderForResuspendingCompleted() {
        if(this.selectedProcessFlow.axiom384Propel && this.selectedProcessFlow.manualPreparation) {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '0', "PLATE"]);
        }
        else {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '1', 'PLATE']);
        }
    }

    deciderForDenaturationStarted() {
        if(this.selectedProcessFlow.axiom384Propel && this.selectedProcessFlow.manualPreparation) {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '0', "PLATE"]);
        }
        else {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '1', 'PLATE']);
        }
    }

    deciderForWaitingForAnalysis() {
        if(this.selectedProcessFlow.resultEvaluation) {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '2', "BEADCHIP"]);
        } else {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '1', 'BEADCHIP']);
        }
    }

    deciderForComplete() {
        if(this.selectedPlate.type == 'BEADCHIP') {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '1', 'BEADCHIP']);
        } else {
            this.router.navigate(['/samples', this.selectedPlate.plateID, '0', 'PLATE']);
        }
    }

    // TABBERS
    // Functions that can directly take you to tab
    goToPreparationQCTab() {
        this.router.navigate(['/preparation', this.selectedPlate.plateID, '0']);
    }

    goToPreparationExtractionTab() {
        this.router.navigate(['/preparation', this.selectedPlate.plateID,  '1']);
    }

    goToSamplesPlatePreparationTab() {
        this.router.navigate(['/samples', this.selectedPlate.plateID, '0', 'PLATE']);
    }

    goToArrayPreparationTabAsNormalPlate() {
        this.router.navigate(['/samples', this.selectedPlate.plateID, '1', 'PLATE']);
    }

    goToArrayPreparationTabAsBeadchip() {
        this.router.navigate(['/samples', this.selectedPlate.plateID, '1', 'BEADCHIP']);
    }

    goToResultEvaluationTab(){
        this.router.navigate(['/samples', this.selectedPlate.plateID, '2', 'BEADCHIP']);
    }
}
